/**
 * The shared Plan Overview's grid, as rows. Pure, no I/O, no React.
 *
 * Groups the shared payload's Plan Lines under their Workstreams in the order the working
 * grid shows them — the server's order, for both — and sums each group's hours per month
 * for its header band. The sums are exact: hours are added as integer centi-hours, never
 * as floats, the same way every other plan total is.
 *
 * A group with no lines is dropped. On the working grid an empty Workstream is somewhere
 * to type; on a page a plan is shared as it reads as an unfinished plan, which is why the
 * client export drops them too (src/lib/plan-export/model.ts).
 */

import { formatHoursCenti, parseHoursCenti } from "@/lib/resource-plan-math";
import type {
  SharedPlanLineView,
  SharedPlanView,
  SharedPlanWorkstreamView,
} from "@/schemas/resource-plans";

export interface SharedGridLine {
  line: SharedPlanLineView;
  /** month → the line's hours that month. Absent means no cell. */
  hoursByMonth: ReadonlyMap<string, string>;
}

export interface SharedGridGroup {
  workstream: SharedPlanWorkstreamView;
  lines: SharedGridLine[];
  /** month → the group's hours that month. Absent means no line has a cell there. */
  hoursByMonth: ReadonlyMap<string, string>;
}

export function sharedGridGroups(
  plan: Pick<SharedPlanView, "workstreams" | "lines">,
): SharedGridGroup[] {
  const linesByWorkstream = new Map<string | null, SharedPlanLineView[]>();
  for (const line of plan.lines) {
    const list = linesByWorkstream.get(line.workstreamId) ?? [];
    list.push(line);
    linesByWorkstream.set(line.workstreamId, list);
  }

  const groups: SharedGridGroup[] = [];
  for (const workstream of plan.workstreams) {
    const lines = linesByWorkstream.get(workstream.id) ?? [];
    if (lines.length === 0) continue;

    const centiByMonth = new Map<string, number>();
    for (const line of lines) {
      for (const cell of line.months) {
        centiByMonth.set(
          cell.month,
          (centiByMonth.get(cell.month) ?? 0) + parseHoursCenti(cell.hours),
        );
      }
    }

    groups.push({
      workstream,
      lines: lines.map((line) => ({
        line,
        hoursByMonth: new Map(line.months.map((cell) => [cell.month, cell.hours])),
      })),
      hoursByMonth: new Map(
        [...centiByMonth].map(([month, centi]) => [month, formatHoursCenti(centi)]),
      ),
    });
  }
  return groups;
}
